import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScribbleUnderline } from "@/components/site/Scribbles";
import { cx } from "@/components/site/primitives";
import beeFace from "@/assets/bee-face.png";

// Shared ScribbleReads logo — one source of truth for the mascot badge +
// wordmark lockup, used in AppHeader, the landing Navbar, SignIn and
// Footer. Keeps the bee mascot untouched; only the surrounding badge,
// typography and motion are the "brand" layer.

const SIZES = {
  sm: { badge: "h-8 w-8", word: "text-base", gap: "gap-2", spark: "h-2.5 w-2.5" },
  md: { badge: "h-9 w-9", word: "text-lg", gap: "gap-2.5", spark: "h-3 w-3" },
  lg: { badge: "h-11 w-11", word: "text-xl", gap: "gap-3", spark: "h-3.5 w-3.5" },
};

export default function Logo({
  to = "/app/explore",
  href,
  size = "md",
  variant = "full", // full | compact | icon
  mono = false,
  className = "",
  testId = "app-logo",
}) {
  const [hovered, setHovered] = useState(false);
  const s = SIZES[size] ?? SIZES.md;
  const Comp = href ? "a" : Link;
  const linkProps = href ? { href } : { to };

  return (
    <Comp
      {...linkProps}
      className={cx("group inline-flex items-center", s.gap, className)}
      aria-label="ScribbleReads home"
      data-testid={testId}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      <motion.span
        className={cx(
          "relative grid shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-sage/60 to-sage/25 shadow-soft ring-1 ring-inset ring-coral/20 dark:from-sage/25 dark:to-sage/10 dark:ring-coral/25",
          s.badge,
          mono && "grayscale"
        )}
        animate={{ rotate: hovered ? -8 : 0, scale: hovered ? 1.05 : 1 }}
        transition={{ type: "spring", stiffness: 320, damping: 18 }}
      >
        <img src={beeFace} alt="ScribbleReads bee" className="h-full w-full object-cover" draggable="false" />

        {variant !== "icon" && (
          <motion.svg
            viewBox="0 0 24 24"
            className={cx("absolute -bottom-0.5 -right-0.5 rounded-full bg-cream p-[3px] text-coral shadow-sm dark:bg-card", s.spark)}
            aria-hidden="true"
            initial={false}
            animate={{ rotate: hovered ? 90 : 0, scale: hovered ? 1.12 : 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <path d="M12 3v18M3 12h18" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" fill="none" />
          </motion.svg>
        )}
      </motion.span>

      {variant === "icon" ? null : variant === "compact" ? (
        <span className={cx("shrink-0 whitespace-nowrap font-display font-semibold tracking-tight text-ink", s.word, mono && "text-current")}>
          ScribbleReads
        </span>
      ) : (
        <span className={cx("shrink-0 whitespace-nowrap font-display font-semibold tracking-tight text-ink", s.word, mono && "text-current")}>
          Scribble
          <span className={cx("relative inline-block", mono ? "text-current" : "text-coral")}>
            Reads
            {!mono && (
              <ScribbleUnderline
                inView={hovered}
                className="pointer-events-none absolute -bottom-1.5 left-0 h-2 w-full text-coral"
              />
            )}
          </span>
        </span>
      )}
    </Comp>
  );
}
